import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Coins, Loader2, Sparkles, AlertTriangle, ArrowRight, Wallet, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

/**
 * Inline wallet top-up card rendered in Astrid chat when a credits tool
 * fires (`check_credits` / `quote_migration_credits` / `start_credit_topup`).
 *
 * Behavior:
 *  - Live-reads the wallet on mount + every 30s (keeps the balance fresh even
 *    after a background top-up).
 *  - Buying a pack calls POST /api/credits/checkout (server-priced) and
 *    redirects to Stripe. Before redirect we stash the current conv_id so
 *    the /credits/success page can offer "Return to Astrid" and resume the
 *    exact conversation.
 *
 * Props:
 *   card: {
 *     card: 'wallet_topup', mode: 'info'|'quote'|'topup',
 *     org_id, wallet, packages, suggested_package_id,
 *     shortfall_credits, shortfall_usd, quote?, journey_id?, context
 *   }
 *   conv_id: string   parent chat id (used for the resume flow)
 */
export const WalletTopupCard = ({ card, conv_id }) => {
  const orgId = card?.org_id || getCurrentAccount()?.id;
  const [wallet, setWallet] = useState(card?.wallet || null);
  const [packages, setPackages] = useState(card?.packages || []);
  const [selected, setSelected] = useState(card?.suggested_package_id || 'growth');
  const [busy, setBusy] = useState(false);

  const params = useMemo(() => ({ params: { account_id: orgId } }), [orgId]);

  const refresh = useCallback(() => {
    if (!orgId) return;
    api.get('/credits/wallet', params)
      .then(({ data }) => setWallet(data))
      .catch(() => {});
    if (!packages.length) {
      api.get('/credits/packages', params)
        .then(({ data }) => setPackages(data.packages || []))
        .catch(() => {});
    }
  }, [orgId, params, packages.length]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const t = setInterval(refresh, 30000);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [refresh]);

  const buy = async () => {
    if (!orgId || !selected) return;
    setBusy(true);
    try {
      // Stash the resume marker so /credits/success can bring the user back
      // into THIS chat with the right resume prompt for Astrid.
      try {
        window.localStorage.setItem('ai_agent_resume', JSON.stringify({
          conv_id: conv_id || null,
          reason: card?.context?.reason || 'topup',
          prompt: 'I just topped up — please continue where we left off.',
          expires_at: Date.now() + 1000 * 60 * 30, // 30 min TTL
        }));
      } catch { /* private-mode ignore */ }
      const originUrl = window.location.origin;
      const { data } = await api.post(
        '/credits/checkout',
        { package_id: selected, origin_url: originUrl },
        params,
      );
      if (!data?.url) throw new Error('No checkout URL returned');
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Could not start checkout');
      setBusy(false);
    }
  };

  const bal = wallet?.balance_credits ?? 0;
  const balUsd = wallet?.balance_usd ?? 0;
  const shortfall = Number(card?.shortfall_credits || 0);
  const shortfallUsd = Number(card?.shortfall_usd || 0);
  const mode = card?.mode || 'topup';
  const covered = shortfall <= 0;
  const exempt = Boolean(card?.exempt);

  // Platform staff (Super Admin / Owner) — informational only, no CTA.
  if (exempt) {
    return (
      <div data-testid="ai-wallet-topup-card" className="mt-3 rounded-lg border border-slate-200 bg-white p-3 not-prose">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900 mb-2">
          <Wallet className="h-4 w-4 text-emerald-600" /> Wallet
          <Badge variant="outline" className="rounded-none text-[10px]" data-testid="wallet-card-exempt-badge">
            Platform staff — exempt
          </Badge>
        </div>
        <div data-testid="wallet-card-balance" className="flex items-center justify-between rounded-none border border-slate-200 bg-slate-50 p-2 text-xs">
          <span className="text-slate-600">Org balance (FYI)</span>
          <span className="font-semibold text-slate-800 inline-flex items-center gap-1">
            <Coins className="h-3.5 w-3.5 text-amber-500" />
            {bal.toFixed(1)} credits <span className="text-slate-500 font-normal">(≈ ${balUsd.toFixed(2)})</span>
          </span>
        </div>
        <p className="mt-2 text-[11px] text-slate-500 inline-flex items-center gap-1.5">
          <ShieldCheck className="h-3 w-3 text-emerald-600" />
          Your work here doesn&apos;t spend this org&apos;s credits.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="ai-wallet-topup-card" className="mt-3 rounded-lg border border-slate-200 bg-white p-3 not-prose">
      {/* Header */}
      <div className="flex items-center gap-2 text-sm font-medium text-slate-900 mb-2">
        <Wallet className="h-4 w-4 text-emerald-600" /> Wallet
        <Badge variant="outline" className="rounded-none text-[10px] capitalize">
          {mode === 'quote' ? 'migration quote' : mode}
        </Badge>
      </div>

      {/* Balance strip */}
      <div data-testid="wallet-card-balance" className={`flex items-center justify-between rounded-none border p-2 text-xs mb-2 ${wallet?.empty ? 'border-rose-300 bg-rose-50' : wallet?.low_balance ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
        <span className="text-slate-600">Current balance</span>
        <span className="font-semibold text-slate-800 inline-flex items-center gap-1">
          <Coins className="h-3.5 w-3.5 text-amber-500" />
          {bal.toFixed(1)} credits <span className="text-slate-500 font-normal">(≈ ${balUsd.toFixed(2)})</span>
        </span>
      </div>

      {/* Quote / shortfall banner (only for quote mode) */}
      {mode === 'quote' && card?.quote && (
        <div className="mb-2 grid grid-cols-3 gap-1.5 text-[11px]">
          <div className="rounded border border-slate-100 bg-slate-50 p-1.5 text-center" data-testid="wallet-card-quote">
            <div className="text-[9px] uppercase text-slate-500 tracking-wide">Quote</div>
            <div className="text-sm font-bold text-slate-900">{Number(card.quote.est_credits || 0).toFixed(0)} cr</div>
            <div className="text-[10px] text-slate-500">≈ ${Number(card.quote.est_usd || 0).toFixed(2)}</div>
          </div>
          <div className="rounded border border-slate-100 bg-slate-50 p-1.5 text-center">
            <div className="text-[9px] uppercase text-slate-500 tracking-wide">You have</div>
            <div className="text-sm font-bold text-slate-900">{bal.toFixed(0)} cr</div>
            <div className="text-[10px] text-slate-500">≈ ${balUsd.toFixed(2)}</div>
          </div>
          <div className={`rounded p-1.5 text-center ${covered ? 'border border-emerald-200 bg-emerald-50' : 'border border-rose-200 bg-rose-50'}`} data-testid="wallet-card-shortfall">
            <div className={`text-[9px] uppercase tracking-wide ${covered ? 'text-emerald-700' : 'text-rose-700'}`}>{covered ? 'Covered' : 'Shortfall'}</div>
            <div className={`text-sm font-bold ${covered ? 'text-emerald-800' : 'text-rose-800'}`}>{covered ? '✓' : `${shortfall.toFixed(0)} cr`}</div>
            {!covered && <div className="text-[10px] text-rose-700">≈ ${shortfallUsd.toFixed(2)}</div>}
          </div>
        </div>
      )}

      {/* Package tiles */}
      {(mode !== 'info' || wallet?.low_balance || wallet?.empty) && (
        <div className="grid grid-cols-2 gap-1.5 mb-2" data-testid="wallet-card-packages">
          {packages.map((p) => {
            const active = selected === p.id;
            const isSuggested = p.id === card?.suggested_package_id;
            const totalCredits = p.credits + (p.bonus_credits || 0);
            return (
              <button
                key={p.id}
                type="button"
                data-testid={`wallet-card-pkg-${p.id}`}
                onClick={() => setSelected(p.id)}
                className={`relative text-left rounded-none border-2 p-2 transition-all ${
                  active
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 hover:border-slate-400 bg-white'
                }`}
              >
                {isSuggested && (
                  <span className="absolute -top-1.5 -right-1.5 rounded-none bg-emerald-600 text-white px-1.5 py-0.5 text-[9px] font-bold">
                    Recommended
                  </span>
                )}
                <div className="flex items-baseline justify-between">
                  <span className="text-xs font-bold text-slate-900">{p.label}</span>
                  <span className="text-sm font-black text-slate-900">${p.usd.toFixed(0)}</span>
                </div>
                <div className="text-[10px] text-slate-600 mt-0.5">
                  <span className="font-semibold text-slate-800">{totalCredits}</span> cr
                  {p.bonus_credits > 0 && <span className="ml-1 text-emerald-700">+{p.bonus_credits}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {(mode !== 'info' || wallet?.low_balance || wallet?.empty) && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] text-slate-400 inline-flex items-center gap-1">
            <ShieldCheck className="h-3 w-3" /> secured by Stripe
          </span>
          <Button
            size="sm"
            className="rounded-none h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700"
            onClick={buy}
            disabled={busy || !selected}
            data-testid="wallet-card-buy"
          >
            {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
            Buy &amp; resume in chat <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      )}

      {mode === 'info' && !(wallet?.low_balance || wallet?.empty) && (
        <div className="text-[11px] text-slate-500 inline-flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3 text-slate-400" />
          You're all set — no top-up needed right now.
        </div>
      )}
    </div>
  );
};

export default WalletTopupCard;
